import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { paragraphRegular, paragraphSmall } from '@helpers/typography';
import SearchboxStyles from '@stories/Components/Misc/Forms/Searchbox/Searchbox.styles';
import { ContentType } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { rgba } from 'polished';
import styled, { createGlobalStyle } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --announcementHeight: ${fluid(28, 48)};
    --navigationHeight: 48px;
    --tabsHeight: 38px;
    
    --headerHeight: calc(var(--announcementHeight) + var(--navigationHeight));

    @media ${from(Device.TabletLarge)} {
      --navigationHeight: 112px;
    }
  }
`;
const Container = styled.header `
  ${transition('transform')};

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${Layers.Navigation};
  height: var(--headerHeight);

  &[data-reduce='true'] {
    transform: translateY(calc(var(--announcementHeight) * -1));
  }

  /* Use pseudo elements to ensure that the background colours cover the entire screen width */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    z-index: -1;
  }

  &::before {
    top: 0;
    height: var(--announcementHeight);
    background-color: ${brand.blue.navy};
  }

  &::after {
    top: var(--announcementHeight);
    height: var(--navigationHeight);
    background-color: ${brand.global.white};
    box-shadow: 0px 1px 4px 0px ${rgba(brand.blue.navy, 0.1)};
  }
`;
const Backdrop = styled.button `
  ${ButtonReset};
  ${transition('opacity, backdrop-filter')};

  position: fixed;
  z-index: ${Layers.Navigation - 1};
  top: var(--headerHeight, 0px);
  left: 0;
  width: 100%;
  height: calc(100dvh - var(--headerHeight, 0px));
  background-color: ${rgba(brand.global.black, 0.46)};
  cursor: pointer;
  backdrop-filter: blur(4px);
  cursor: not-allowed;

  &[data-visible='false'] {
    opacity: 0;
    pointer-events: none;
    backdrop-filter: blur(0px);
  }
`;
const Inner = styled.div `
  display: contents;

  @media ${from(Device.TabletLarge)} {
    ${siteWide};

    display: grid;
    grid-template-columns: 1fr auto 1fr;
    column-gap: 24px;
  }
`;
const NavigationTabs = styled.div `
  display: flex;
  align-items: stretch;
  background-color: ${brand.blue.navy};
  height: var(--tabsHeight);
  padding-left: ${fluid(18, 64)};

  @media ${until(Device.TabletLarge)} {
    ${transition('transform, opacity')};

    position: relative;
    z-index: -2; // Ensure the tabs slide out from beneath the main body

    &[data-mobile='false'] {
      display: none;
    }

    ${Container}[data-menu-open='false'] & {
      opacity: 0;
      transform: translateY(-100%);
      pointer-events: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    &[data-mobile='true'] {
      display: none;
    }

    height: var(--announcementHeight);
    grid-column: 1;
  }
`;
const NavigationTab = styled.a `
  ${paragraphSmall};
  ${fonts.proximaNova.medium};
  ${transition('color')};

  display: flex;
  align-items: center;
  padding: 0 ${fluid(18, 22)};
  margin: 0;
  color: ${brand.global.white};
  text-decoration: none;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: auto;
    outline-offset: -2px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: ${brand.green.default};
  }

  &:focus-visible,
  &:hover {
    color: ${brand.green.default};
  }

  &[aria-selected='true'] {
    color: ${brand.green.dark};
    outline-color: ${brand.green.dark};
  }

  &[aria-selected='true'] {
    ${fonts.proximaNova.bold};

    background-color: ${brand.global.white};
  }
`;
const Main = styled.div `
  display: flex;
  height: var(--navigationHeight);
  background-color: ${brand.global.white};
  padding: 6px var(--sitePadding);

  @media ${from(Device.TabletLarge)} {
    position: relative;
    grid-column: 1 / -1;
    grid-row: 2;
    padding: 0;
    gap: ${fluid(24, 76)};
  }
`;
const Logo = styled.a `
  display: block;
  width: ${fluid(74, 167, Device.DesktopSmall)};

  svg {
    display: block;
    height: 100%;
  }

  @media ${from(Device.TabletLarge)} {
    margin: 12px 0;
    display: flex;
    align-items: center;
  }
`;
const CtaWrapper = styled.div `
  display: flex;
  gap: ${fluid(22, 54, Device.Desktop)};
  margin-left: auto;
  align-items: center;
  order: 2;
`;
const SearchBoxContainer = styled.div `
  ${transition('transform, opacity')};

  position: absolute;
  top: var(--headerHeight, 0px);
  left: 0;
  z-index: -2; // Ensure the search slides out from beneath the main body
  padding: 8px var(--sitePadding);
  background-color: ${brand.global.white};
  width: 100%;
  border-bottom-right-radius: ${fluid(16, 32)};

  @media ${until(Device.DesktopSmall)} {
    &[data-open='false'] {
      opacity: 0;
      transform: translateY(-100%);
      pointer-events: none;
    }

    @media ${from(Device.TabletLarge)} {
      top: 100%;
      padding: 8px;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    display: contents;

    ${SearchboxStyles.Container} {
      flex: 2 1 340px;
      max-width: 340px;

      ${CtaWrapper}:not(:has(${() => CtaLink}[data-mobile="false"])) & {
        min-width: 280px;
        flex-grow: 3;
      }
    }

    ${SearchboxStyles.Button}[data-type='clear'] {
      display: none;
    }
  }
`;
const CtaLink = styled.a `
  ${transition('text-decoration-color, color')};
  ${ButtonReset};
  ${paragraphRegular};
  ${fonts.proximaNova.bold};

  --primaryColour: ${brand.green.default};
  --contrastColour: ${brand.global.white};

  display: flex;
  align-items: center;
  height: ${fluid(28, 42)};
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
  color: ${brand.blue.navy};
  margin: 0;
  text-align: left;

  &[data-type=${ContentType.Intermediaries}] {
    --primaryColour: ${brand.green.light};
    --contrastColour: ${brand.blue.navy};
  }

  &[data-type=${ContentType.Savings}] {
    --primaryColour: ${brand.blue.default};
  }

  svg {
    display: block;
    width: ${fluid(18, 24)};
  }

  span:nth-child(1) {
    ${transition('background-color, color')};

    display: grid;
    place-content: center;
    border-radius: 5000px;
    height: 100%;
    aspect-ratio: 1;
    background-color: var(--primaryColour);
    color: var(--contrastColour);
    border: 2px solid var(--primaryColour);
  }

  span:nth-child(2) {
    @media ${until(Device.DesktopSmall)} {
      ${srOnly};
    }
  }

  &:focus-visible,
  &:hover {
    text-decoration-color: currentColor;

    span:nth-child(1) {
      color: var(--primaryColour);
      background-color: var(--contrastColour);
    }
  }

  &[aria-expanded] {
    svg {
      ${transition('transform, opacity')};

      grid-area: 1 / 1 / 1 / 1;
      margin: auto;
    }

    svg:nth-child(2) {
      width: 12px;
    }
  }

  &[aria-expanded='false'] {
    svg:nth-child(2) {
      transform: scale(0.5);
      opacity: 0;
    }
  }

  &[aria-expanded='true'] {
    svg:nth-child(1) {
      transform: scale(0.5);
      opacity: 0;
    }
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  @media ${from(Device.DesktopSmall)} {
    white-space: nowrap;
    gap: 8px;

    &[data-style='solid'] {
      ${transition('background-color, color')};

      color: var(--contrastColour);
      background-color: var(--primaryColour);
      text-decoration: none;
      border-radius: 30px;
      padding: 0 16px;
      border: 2px solid var(--primaryColour);

      span:nth-child(1) {
        display: contents;
      }

      &:focus-visible,
      &:hover {
        color: var(--primaryColour);
        background-color: var(--contrastColour);
      }
    }

    &[data-mobile='true'] {
      display: none;
    }
  }
`;
const MenuButton = styled.button `
  ${transition('background-size, border-color, color, transform', 0.15)};
  ${ButtonReset};

  display: grid;
  height: 24px;
  width: 24px;
  color: ${brand.blue.navy};

  svg {
    ${transition('transform, opacity')};

    display: block;
    margin: auto 0 auto auto;
    grid-area: 1 / 1 / 1 / 1;

    &:nth-child(1) {
      width: 21px;
    }

    &:nth-child(2) {
      width: 17px;
    }
  }

  &[aria-expanded='false'] {
    svg:nth-child(2) {
      transform: scale(0.5);
      opacity: 0;
    }
  }

  &[aria-expanded='true'] {
    svg:nth-child(1) {
      transform: scale(0.5);
      opacity: 0;
    }
  }

  &:focus-visible {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
  }

  /* button should mimic press */
  &:active {
    transform: translateY(1.5px);
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const NavigationStyles = {
    Backdrop,
    Container,
    CtaLink,
    CtaWrapper,
    GlobalStyles,
    Inner,
    Logo,
    Main,
    MenuButton,
    NavigationTab,
    NavigationTabs,
    SearchBoxContainer,
};
export default NavigationStyles;
